import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Zap, Users, BarChart3 } from "lucide-react";
import "./LandingPage.scss";

// Helper to get subdomain
const getSubdomain = (hostname: string): string => {
  const parts = hostname.split('.');
  // For "a.business.chargingadda.com" → return "a"
  // For "business.chargingadda.com" → return "" (no subdomain)
  if (parts.length > 3 || (parts.length === 3 && parts[1] === 'business')) {
    return parts[0];
  }
  return "";
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user came from logout callback
    const currentHostname = window.location.hostname;
    const currentSubdomain = getSubdomain(currentHostname);
    
    // If on main domain, check for return subdomain from logout
    if (!currentSubdomain || currentHostname === "business.chargingadda.com") {
      console.log("📍 Landing on main domain, checking for logout redirect...");
      
      // Check localStorage for logout return subdomain
      let returnSubdomain = localStorage.getItem("logout_return_subdomain");
      
      // Check cookie as backup
      if (!returnSubdomain) {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'logout_subdomain') {
            returnSubdomain = value;
            break;
          }
        }
      }
      
      if (returnSubdomain) {
        console.log("✅ Found return subdomain from logout:", returnSubdomain);
        
        // Clean up storage
        localStorage.removeItem("logout_return_subdomain");
        document.cookie = "logout_subdomain=; path=/; domain=.business.chargingadda.com; max-age=0";
        
        // Redirect to org subdomain login
        const redirectUrl = `https://${returnSubdomain}.business.chargingadda.com/login`;
        console.log("🔄 Redirecting to:", redirectUrl);
        window.location.href = redirectUrl;
        return;
      }
    }
    
    // If user is logged in, check if they should be redirected to dashboard
    const token = localStorage.getItem("access_token");
    if (token) {
      console.log("✅ User is logged in, redirecting to dashboard");
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleLoginClick = () => {
    const currentHostname = window.location.hostname;
    const currentSubdomain = getSubdomain(currentHostname);
    
    // If on subdomain, go to that subdomain's login
    if (currentSubdomain) {
      navigate("/login");
    } else {
      // If on main domain, can't login without org - show message
      alert("Please use your organization's URL (e.g., yourorg.business.chargingadda.com) to login");
    }
  };

  return (
    <div className="landingpage">
      {/* ===== HEADER ===== */}
      <header className="header">
        <div className="logo">
          <img src="https://www.chargingadda.com/assets/logo-CxiJ9doh.png" alt="ChargingAdda Logo" className="logo-image" />
          <div className="logo-text">
            <h1>ChargingAdda</h1>
          </div>
        </div>
        <button className="login-btn" onClick={handleLoginClick}>
          Login
        </button>
      </header>

      {/* ===== HERO SECTION ===== */}
      <section className="hero">
        <div className="content">
          <h1>Smart Charging Management Platform</h1>
          <p>
            Manage your EV charging network with ease — monitor stations, track
            revenue, control access, and grow your business — all in one CMS
            dashboard.
          </p>
          <button onClick={handleLoginClick}>
            Get Started
          </button>
        </div>
        <div className="hero-image">
          <img
            src="https://www.chargingadda.com/assets/solution-residential-DdUQVs4M.png"
            alt="Charging Station"
          />
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="features">
        <h2>Powerful CMS Features</h2>
        <div className="feature-list">
          {[
            {
              title: "Revenue Dashboard",
              desc: "Visualize your daily, weekly, and monthly earnings through interactive charts.",
              icon: TrendingUp,
            },
            {
              title: "Station Management",
              desc: "Add, update, or remove your charging stations with just a few clicks.",
              icon: Zap,
            },
            {
              title: "User Roles (RBAC)",
              desc: "Assign roles for organizations, fleet managers, or admins securely.",
              icon: Users,
            },
            {
              title: "Smart Analytics",
              desc: "Real-time analytics to help you understand performance and optimize charging.",
              icon: BarChart3,
            },
          ].map((f, i) => {
            const IconComponent = f.icon;
            return (
              <div className="feature-card" key={i}>
                <div className="icon-wrapper">
                  <IconComponent size={48} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="about">
        <div className="text">
          <h2>About ChargingAdda CMS</h2>
          <p>
            ChargingAdda CMS is a comprehensive EV charging management platform
            for organizations, fleets, and businesses. It empowers you with
            complete control over stations, users, and revenue — providing
            analytics, reports, and a seamless management experience.
          </p>
        </div>
        <img
          src="https://www.chargingadda.com/assets/logo-CxiJ9doh.png"
          alt="About ChargingAdda"
        />
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <p>
          © {new Date().getFullYear()} ChargingAdda CMS — All Rights Reserved
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;